import { getArgFromUrl } from '@dana/components';

export const SUPPORTED_LANGUAGES = ['en', 'ar', 'tr', 'es', 'pt', 'ru', 'uz'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const SUPPORTED_LANGUAGE_SET = new Set<string>(SUPPORTED_LANGUAGES);
const MANUAL_LANGUAGE_STORAGE_KEY = 'recharge-pages.language';

export function normalizeLanguageCode(code?: string | null): SupportedLanguage | undefined {
  if (!code) return undefined;

  const normalized = code.trim().toLowerCase().replace('_', '-');
  if (!normalized) return undefined;

  if (SUPPORTED_LANGUAGE_SET.has(normalized)) {
    return normalized as SupportedLanguage;
  }

  const baseCode = normalized.split('-')[0];
  if (SUPPORTED_LANGUAGE_SET.has(baseCode)) {
    return baseCode as SupportedLanguage;
  }

  return undefined;
}

function getBrowserLanguage(): SupportedLanguage | undefined {
  if (typeof navigator === 'undefined') return undefined;

  const candidates =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];

  for (const candidate of candidates) {
    const resolved = normalizeLanguageCode(candidate);
    if (resolved) return resolved;
  }

  return undefined;
}

function getManualLanguage(): SupportedLanguage | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    return normalizeLanguageCode(window.localStorage.getItem(MANUAL_LANGUAGE_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

export function setManualLanguage(code?: string | null): SupportedLanguage | undefined {
  const normalized = normalizeLanguageCode(code);
  if (typeof window === 'undefined') return normalized;

  try {
    if (normalized) {
      window.localStorage.setItem(MANUAL_LANGUAGE_STORAGE_KEY, normalized);
    } else {
      window.localStorage.removeItem(MANUAL_LANGUAGE_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors (private mode, disabled storage, etc.)
  }

  return normalized;
}

export function getInitialLanguage(): SupportedLanguage {
  const manualLanguage = getManualLanguage();
  if (manualLanguage) return manualLanguage;

  const urlLanguage = normalizeLanguageCode(getArgFromUrl('language'));
  if (urlLanguage) return urlLanguage;

  return getBrowserLanguage() || 'en';
}
